import ActionBarItem from '@rio-cloud/rio-uikit/ActionBarItem';
import Button from '@rio-cloud/rio-uikit/Button';
import Divider from '@rio-cloud/rio-uikit/Divider';
import Select, { type SelectOption } from '@rio-cloud/rio-uikit/Select';
import Switch from '@rio-cloud/rio-uikit/Switch';
import { useIntl } from 'react-intl';

import { config } from '@/config';
import { formattedLanguageName, getSupportedLocale, LanguagesCodes } from '@/configuration/lang/lang';
import { configureFetchDisplayMessages } from '@/configuration/lang/services';
import { createUserManager } from '@/configuration/login/login';
import { useDevStore } from '@/stores/devStore';
import { useLocaleStore } from '@/stores/localeStore';
import { useUserStore } from '@/stores/userStore';

const DevUserMenu = () => {
    const { setCountryIntl } = useUserStore();
    const { displayLocale } = useLocaleStore();
    const { isGADebugEnabled, toogleGADebug } = useDevStore();
    const intl = useIntl();

    const tagAssistantURL = `https://tagassistant.google.com/#/?source=TAG_MANAGER&id=${
        config.googleAnalytics.gtm_id
    }&gtm_auth=${config.googleAnalytics.gtm_auth}&gtm_preview=${
        config.googleAnalytics.gtm_preview
    }&url=${encodeURIComponent(window.location.origin)}`;

    const options = [...LanguagesCodes].sort().map(
        (locale): SelectOption => ({
            id: locale,
            label: formattedLanguageName(locale),
            selected: locale === displayLocale,
        })
    );

    const handleGAToggle = () => {
        toogleGADebug();

        if (!isGADebugEnabled) {
            window.open(tagAssistantURL, '_blank');
        }
    };

    const handleLanguageChange = async (option: SelectOption | undefined) => {
        if (!option) {
            return;
        }

        try {
            const parsedLocale = getSupportedLocale(option.id);
            const fetchMessages = configureFetchDisplayMessages();
            await fetchMessages(parsedLocale);

            // Store the selected language in localStorage for persistence
            localStorage.setItem('preferred-locale', parsedLocale);
            setCountryIntl(parsedLocale, intl);
        } catch (error) {
            console.error('Failed to change language:', error);
        }
    };

    const handleLogout = async () => await createUserManager().signoutRedirect();

    return (
        <ActionBarItem hidePopoverOnClick={false} popoverWidth={400}>
            <ActionBarItem.Icon>
                <i className='icon uikit-icon icon-user' />
            </ActionBarItem.Icon>
            <ActionBarItem.Popover
                id='dev-user-menu'
                title={
                    <>
                        <h6>DEV User Menu</h6>
                        <span>{config.serviceEnvironment.toUpperCase()}</span>
                    </>
                }
            >
                <div className='display-flex flex-row align-items-center gap-5 justify-content-between'>
                    <label>
                        GA -{' '}
                        <a target='_blank' href={tagAssistantURL} rel='noreferrer' title='Open Tag Assistant'>
                            Tag Assistant{' '}
                        </a>
                    </label>
                    <Switch
                        enabledText='GA Debugger'
                        checked={isGADebugEnabled}
                        onChange={handleGAToggle}
                        labelPosition={Switch.LABEL_POSITION_RIGHT}
                    />
                </div>
                <Divider />
                <div className='display-flex flex-column gap-5'>
                    <label>Current Language: {formattedLanguageName(displayLocale ?? '')}</label>
                    <Select options={options} placeholder='Select a language' onChange={handleLanguageChange} />
                </div>

                <Divider />
                <div className='display-flex justify-content-end'>
                    <Button onClick={handleLogout}>Logout</Button>
                </div>
            </ActionBarItem.Popover>
        </ActionBarItem>
    );
};

export default DevUserMenu;
